import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Outlook OAuth connection status.
 */
export function useOutlookStatus() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['outlook', 'status'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/oauth/outlook/status', getAuthHeader());
                return {
                    connected: data.connected,
                    needs_reconnect: data.needs_reconnect || false,
                    email: data.outlook_email || '',
                };
            } catch (err) {
                const status = err?.response?.status;
                if (status === 401 || status === 404) {
                    return { connected: false, needs_reconnect: false, email: '' };
                }
                throw err;
            }
        },
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Outlook emails from the connected Outlook account.
 * Only enabled when Outlook is connected. Polls every 60s.
 */
export function useOutlookEmails(outlookConnected) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['outlook', 'emails'],
        queryFn: async () => {
            const data = await apiFetch('/oauth/outlook/emails?max_results=30', getAuthHeader());
            return (data.emails || []).map(e => ({
                ...e,
                _source: 'outlook',
                email_id: `outlook_${e.id}`,
                contact_name: e.from?.replace(/<.*>/, '').trim() || '',
                contact_email: e.from?.match(/<(.+)>/)?.[1] || e.from || '',
                subject: e.subject,
                body: e.snippet,
                status: e.is_unread ? 'unread' : 'read',
                sent_at: e.date,
            }));
        },
        enabled: !!outlookConnected,
        staleTime: 2 * 60 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Sync Outlook emails to CRM communications.
 * Mirrors useSyncGmail (useInbox.js:249-265) — axios.get, NOT apiFetch:
 * apiFetch(url, headers) is GET-only and takes exactly two args (lib/api.js:9-12),
 * a third options arg is silently ignored.
 */
export function useSyncOutlook() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            axios.get(`${API}/oauth/outlook/sync`, {
                headers: getAuthHeader(),
                params: { max_results: 50 },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['outlook', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['communications'] });
        },
    });
}
